// Holds the active theme and appearance, writes the two root attributes and the `dark`
// class, and keeps the stored setting. Plain JavaScript with every outside thing passed
// in (storage, root element, media query, logger), so tests drive it without a browser.
// src/hooks/useTheme.jsx wraps one shared instance for React.
import { THEMES, APPEARANCE_SETTINGS, DEFAULT_THEME_ID, getTheme, isAvailable } from './registry.js'
import { STORAGE_KEY, LEGACY_STORAGE_KEY, resolveInitial, effectiveAppearance } from './resolve.js'

export const RESET_NOTICE = 'Your saved theme was not recognized, so Original Clairvos was restored.'

const DARK_QUERY = '(prefers-color-scheme: dark)'

export function applyToRoot(root, themeId, appearance) {
  if (!root) return
  root.setAttribute('data-theme', themeId)
  root.setAttribute('data-appearance', appearance)
  root.classList.toggle('dark', appearance === 'dark')
}

// Every storage touch can throw (blocked cookies, private windows), so each one is guarded.
function safeGet(storage, key) {
  try { return storage ? storage.getItem(key) : null } catch { return null }
}
function safeSet(storage, key, value) {
  try { if (storage) storage.setItem(key, value) } catch { /* works with storage blocked: the choice lasts until reload */ }
}

// stylesApplied(themeId) answers whether that theme's package rules are really in the page. It is asked for every
// theme except the default, whose values the console carries itself. Left out, every theme counts as applied.
export function createThemeController({ storage, root, matchMedia, themes = THEMES, log = console, stylesApplied = () => true } = {}) {
  const query = () => { try { return matchMedia ? matchMedia(DARK_QUERY) : null } catch { return null } }
  const prefersDark = () => !!query()?.matches

  const initial = resolveInitial({
    stored: safeGet(storage, STORAGE_KEY),
    legacy: safeGet(storage, LEGACY_STORAGE_KEY),
    prefersDark: prefersDark(),
    themes,
  })

  applyToRoot(root, initial.themeId, initial.appearance)
  if (initial.themeId !== DEFAULT_THEME_ID && !stylesApplied(initial.themeId)) {
    initial.problems.push(`The styles of the saved theme "${initial.themeId}" did not load.`)
    initial.themeId = DEFAULT_THEME_ID
    initial.appearance = effectiveAppearance(getTheme(DEFAULT_THEME_ID, themes), initial.appearanceSetting, prefersDark())
  }

  let state = {
    themeId: initial.themeId,
    appearanceSetting: initial.appearanceSetting,
    appearance: initial.appearance,
    notice: initial.problems.length ? RESET_NOTICE : null,
  }
  const listeners = new Set()
  let stopWatching = null

  const persist = () => safeSet(storage, STORAGE_KEY, JSON.stringify({ theme: state.themeId, appearance: state.appearanceSetting }))

  const commit = (patch, { save = true } = {}) => {
    const next = { ...state, ...patch }
    next.appearance = effectiveAppearance(getTheme(next.themeId, themes), next.appearanceSetting, prefersDark())
    state = next
    applyToRoot(root, state.themeId, state.appearance)
    if (save) persist()
    listeners.forEach((fn) => fn())
  }

  // First run: say loudly what was wrong, then write the clean value so the notice shows once.
  for (const problem of initial.problems) log.error(`[theme] ${problem} Using Original Clairvos.`)
  applyToRoot(root, state.themeId, state.appearance)
  if (initial.problems.length || initial.migrated) persist()

  return {
    getState: () => state,
    subscribe(fn) {
      listeners.add(fn)
      return () => listeners.delete(fn)
    },
    setThemeId(id) {
      if (!getTheme(id, themes)) throw new Error(`Unknown theme "${id}".`)
      if (!isAvailable(id, themes)) throw new Error(`Theme "${id}" is not available yet.`)
      commit({ themeId: id })
      if (id !== DEFAULT_THEME_ID && !stylesApplied(id)) {
        log.error(`[theme] The styles of theme "${id}" did not load. Using Original Clairvos.`)
        commit({ themeId: DEFAULT_THEME_ID, notice: RESET_NOTICE })
      }
    },
    setAppearanceSetting(setting) {
      if (!APPEARANCE_SETTINGS.includes(setting)) throw new Error(`Unknown appearance "${setting}". Use light, dark or system.`)
      commit({ appearanceSetting: setting })
    },
    // The header sun and moon button: flip the appearance you see, as an explicit choice.
    toggleAppearance() {
      const target = state.appearance === 'dark' ? 'light' : 'dark'
      if (!getTheme(state.themeId, themes).appearances.includes(target)) return
      commit({ appearanceSetting: target })
    },
    dismissNotice() {
      if (state.notice === null) return
      state = { ...state, notice: null }
      listeners.forEach((fn) => fn())
    },
    // Follow the device while the setting is `system`. Returns the stop function.
    start() {
      const mql = query()
      if (!mql || typeof mql.addEventListener !== 'function') return () => {}
      const onChange = () => { if (state.appearanceSetting === 'system') commit({}, { save: false }) }
      mql.addEventListener('change', onChange)
      stopWatching = () => mql.removeEventListener('change', onChange)
      return () => { stopWatching?.(); stopWatching = null }
    },
  }
}
